import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Grid,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import md5 from "md5";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import TicketFormError, { PAGE_ERROR_TITLES } from "components/PageErrorAlert";
import DepartmentSelect from "components/DepartmentSelect";

// API utility
import { api } from "utils/api";
import { createClientzoneTicket } from "utils/clientzonePortalApi";

// Date formatting utility
import { formatDateTime } from "utils/dateFormatter";
import { userSatisfiesPolicyKey } from "utils/groupPolicyEngine";

// Rich text editor modules and formats configuration
const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ color: [] }, { background: [] }],
    [{ font: [] }],
    [{ align: [] }],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "color",
  "background",
  "font",
  "align",
];

const CLIENTZONE_CREATE_DEPARTMENTS = ["Billing", "Sales", "Technical"];
const CLIENTZONE_CREATE_PRIORITY = "3";

export default function NewTicketDialog({
  open,
  onClose,
  onSuccess,
  initialCustomer = null,
  lockCustomerSelection = false,
  portalVariant = "admin",
  clientAccessToken = null,
}) {
  const isClientPortal = portalVariant === "client";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [department, setDepartment] = useState("");
  const [priority, setPriority] = useState(CLIENTZONE_CREATE_PRIORITY);
  const [assignedUser, setAssignedUser] = useState("");
  const [message, setMessage] = useState("");
  const [userGroups, setUserGroups] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  const canManageTicketAssignment =
    !isClientPortal &&
    (userSatisfiesPolicyKey(userGroups, "managerOrAdmin") || currentUser?.priv_admin === "Y");

  // Fetch initial data when dialog opens
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (isClientPortal) {
          setCurrentUser(null);
          setUserGroups([]);
          setCustomers(initialCustomer ? [initialCustomer] : []);
          setUsers([]);
          return;
        }

        // Get current user data from localStorage
        const userData = JSON.parse(localStorage.getItem("user"));
        if (!userData) {
          throw new Error("No user data found");
        }
        setCurrentUser(userData);
        setUserGroups(userData.groups || []);

        // Fetch customers
        const customersResponse = await api.post("/db/customers/filter", {
          filters: {},
          columns: ["customer_id", "code", "name", "telephone1"],
          order_by: "code ASC",
        });
        setCustomers(customersResponse || []);

        const canFetchUserDirectory =
          userSatisfiesPolicyKey(userData.groups || [], "managerOrAdmin") ||
          userData.priv_admin === "Y";

        if (canFetchUserDirectory) {
          const usersResponse = await api.post("/db/sec_users/filter", {
            filters: { active: "Y" },
            columns: ["login", "name"],
            order_by: "name ASC",
          });
          setUsers(usersResponse || []);
        }
      } catch (fetchError) {
        console.error("Error fetching data:", fetchError);
        setError(fetchError.message || "Failed to load data");
        setCustomers([]);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      setLoading(true);
      fetchData();
    }
  }, [open, isClientPortal, initialCustomer]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedCustomer(initialCustomer || null);
      setDepartment("");
      setPriority(CLIENTZONE_CREATE_PRIORITY);
      setMessage("");
      setAssignedUser("");
      setError("");
    }
  }, [open, initialCustomer]);

  // Reset assigned user when dialog opens or when assignment privileges change
  useEffect(() => {
    if (!canManageTicketAssignment) {
      setAssignedUser(""); // Set to unassigned for non-managers
    }
  }, [canManageTicketAssignment, open]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError("");

      // Validate required fields
      if (!selectedCustomer) {
        setError("Please select a customer");
        return;
      }

      if (!department) {
        setError("Please select a department");
        return;
      }

      if (!message) {
        setError("Please enter a message");
        return;
      }

      if (isClientPortal) {
        if (!clientAccessToken) {
          setError("Session expired. Please sign in again.");
          return;
        }
        if (!CLIENTZONE_CREATE_DEPARTMENTS.includes(department)) {
          setError("Please select Billing, Sales, or Technical Support.");
          return;
        }
        const created = await createClientzoneTicket(
          {
            department,
            level: parseInt(CLIENTZONE_CREATE_PRIORITY, 10),
            message,
          },
          clientAccessToken
        );
        onSuccess(created?.ticket?.ticket_id || created?.ticket_id);
        return;
      }

      // Validate assignment permissions
      if (!canManageTicketAssignment && assignedUser && assignedUser !== currentUser.login) {
        setError("You can only create unassigned tickets or assign them to yourself");
        return;
      }

      // Format current date for MySQL using the formatDate utility
      const currentDateTime = formatDateTime(new Date());

      // Generate ticket_code using MD5
      const ticketCodeInput = `${selectedCustomer.customer_id}${currentUser.login}${currentDateTime}`;
      const ticketCode = md5(ticketCodeInput);

      // Create ticket object
      const ticketData = {
        ticket_code: ticketCode,
        customer_id: selectedCustomer.customer_id,
        create_user: currentUser.login,
        current_user: assignedUser || null,
        create_datetime: currentDateTime,
        update_datetime: currentDateTime,
        resolved: "No",
        department: department,
        level: parseInt(priority),
      };

      // Create ticket
      const ticketResponse = await api.post("/db/tickets", ticketData);
      const ticketId = ticketResponse.ticket_id;

      // Create initial message
      const messageData = {
        ticket: ticketId,
        user: currentUser.login,
        message: message,
        datetime: currentDateTime,
      };

      await api.post("/db/ticket_messages", messageData);

      // Log ticket creation
      await api.post("/db/ticket_logs", {
        ticket_id: ticketId,
        log_message: `Ticket created by ${currentUser.login}${
          assignedUser ? ` and assigned to ${assignedUser}` : ""
        }`,
      });

      onSuccess(ticketId);
    } catch (submitError) {
      console.error("Error creating ticket:", submitError);
      setError(submitError.message || "Failed to create ticket");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isClientPortal ? "Open Support Ticket" : "Create New Ticket"}</DialogTitle>
      <DialogContent>
        {error && (
          <TicketFormError title={PAGE_ERROR_TITLES.newTicket} message={error} sx={{ mb: 2 }} />
        )}

        <MDBox sx={{ mt: 2 }}>
          {/* Customer Selection */}
          {!isClientPortal ? (
            <Grid item xs={12}>
              <Autocomplete
                options={customers}
                getOptionLabel={(option) =>
                  option
                    ? `[${option.code ? option.code.toUpperCase() : "N/A"}] ${
                        option.name || "Unknown"
                      }`
                    : ""
                }
                value={selectedCustomer}
                onChange={(event, newValue) => {
                  setSelectedCustomer(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Customer"
                    required
                    fullWidth
                    disabled={loading || lockCustomerSelection}
                  />
                )}
                renderOption={(props, option) => (
                  <MenuItem {...props}>
                    [{option.code ? option.code.toUpperCase() : "N/A"}] {option.name || "Unknown"}
                  </MenuItem>
                )}
                isOptionEqualToValue={(option, value) => option?.code === value?.code}
                loading={loading}
                loadingText="Loading customers..."
                noOptionsText="No customers found"
                disabled={lockCustomerSelection}
              />
            </Grid>
          ) : (
            <MDBox mb={2}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Account
              </MDTypography>
              <MDTypography variant="body2">
                {selectedCustomer
                  ? `[${String(selectedCustomer.code || "N/A").toUpperCase()}] ${
                      selectedCustomer.name || "Your account"
                    }`
                  : "Your account"}
              </MDTypography>
            </MDBox>
          )}

          {/* Department Selection */}
          <MDBox sx={{ mb: 3, mt: isClientPortal ? 0 : 2 }}>
            <DepartmentSelect
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              required
              filterByAccess={false}
              allowedValues={isClientPortal ? CLIENTZONE_CREATE_DEPARTMENTS : null}
            />
            <MDTypography variant="caption" color="text" display="block" mt={0.5}>
              Select the department that will handle this ticket
            </MDTypography>
          </MDBox>

          {/* Priority Selection */}
          {isClientPortal ? (
            <MDBox mb={3}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Priority Level
              </MDTypography>
              <MDTypography variant="body2">
                Medium (Level 3) - Standard priority issues
              </MDTypography>
              <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                Customer tickets are opened at medium priority
              </MDTypography>
            </MDBox>
          ) : (
            <TextField
              select
              fullWidth
              label="Priority Level"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              required
              sx={{ mb: 3 }}
              helperText="Select the urgency level of this ticket"
            >
              <MenuItem value="1">
                Urgent (Level 1) - Critical issues requiring immediate attention
              </MenuItem>
              <MenuItem value="2">High (Level 2) - Serious issues affecting operations</MenuItem>
              <MenuItem value="3">Medium (Level 3) - Standard priority issues</MenuItem>
              <MenuItem value="4">Low (Level 4) - Minor issues with no immediate impact</MenuItem>
              <MenuItem value="5">Minor (Level 5) - Trivial issues or general inquiries</MenuItem>
            </TextField>
          )}

          {/* Assigned User Selection - Only show if user is manager/admin */}
          {canManageTicketAssignment && (
            <TextField
              select
              fullWidth
              label="Assigned To"
              value={assignedUser}
              onChange={(e) => setAssignedUser(e.target.value)}
              sx={{ mb: 3 }}
              helperText="Assign this ticket to a specific user or leave unassigned"
            >
              <MenuItem value="">
                <em>Unassigned</em>
              </MenuItem>
              {users.map((user) => (
                <MenuItem key={user.login} value={user.login}>
                  {user.name} ({user.login})
                </MenuItem>
              ))}
            </TextField>
          )}

          {/* Message Editor */}
          <MDBox sx={{ mb: 2 }}>
            <MDTypography variant="caption" color="text" fontWeight="bold">
              Initial Message
            </MDTypography>
            <MDBox
              sx={{
                mt: 1,
                "& .ql-editor": {
                  minHeight: "200px",
                  fontSize: "14px",
                  fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
                },
                "& .ql-toolbar": {
                  borderTopLeftRadius: "10px",
                  borderTopRightRadius: "10px",
                  border: "1px solid rgba(0, 0, 0, 0.23)",
                },
                "& .ql-container": {
                  borderBottomLeftRadius: "10px",
                  borderBottomRightRadius: "10px",
                  border: "1px solid rgba(0, 0, 0, 0.23)",
                  borderTop: "none",
                },
                "& .ql-snow .ql-tooltip": { zIndex: 1500 },
              }}
            >
              <ReactQuill
                value={message}
                onChange={setMessage}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Enter your message here..."
              />
            </MDBox>
            <MDTypography variant="caption" color="text" sx={{ mt: 1, display: "block" }}>
              Provide a detailed description of the issue or request
            </MDTypography>
          </MDBox>
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton onClick={onClose} color="secondary">
          Cancel
        </MDButton>
        <MDButton onClick={handleSubmit} color="info" variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} color="inherit" /> : "Create Ticket"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

// Add prop-types validation
NewTicketDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  initialCustomer: PropTypes.shape({
    customer_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    code: PropTypes.string,
    name: PropTypes.string,
    telephone1: PropTypes.string,
  }),
  lockCustomerSelection: PropTypes.bool,
  portalVariant: PropTypes.oneOf(["admin", "client"]),
  clientAccessToken: PropTypes.string,
};

// Add default props
NewTicketDialog.defaultProps = {
  open: false,
  onClose: () => {},
  onSuccess: () => {},
  initialCustomer: null,
  lockCustomerSelection: false,
  portalVariant: "admin",
  clientAccessToken: null,
};
